import React, { useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select } from "@mui/material";
import { EncodingType } from "../../../redux/api/api.types";
import { EncodingMetadataType, EncodingMethodsTypes, HelperTextVariant } from "../../../utils/enum/enum";
import EncodingDialogMetadata from "./EncodingDialogMetadata";
import HelperText from "../../../components/HelperText/HelperText";
import Label from "../../../components/Label/Label";
import { getEncodingMetadataFields, getLookupMetaDataError, getSGTIN96MetadataError } from "./encodingFunctions";

type EncodingDialogProps = {
  index: number | undefined;
  encodings: EncodingType[];
  handleClose: (value: EncodingType | null) => void;
}

export default function EncodingDialog({
  index,
  encodings,
  handleClose,
}: EncodingDialogProps) {
  const [encodingType, setEncodingType] = useState<EncodingType | null>(null);

  const encodingMethods = encodings.map(({ method }) => method);
  const selectableEncodings = Object.entries(EncodingMethodsTypes).map(([key, value]) => ({
    key,
    value,
    disabled: value !== EncodingMethodsTypes.SGTIN96 && encodingMethods.includes(value)
  }))

  useEffect(() => {
    if (index === undefined || index === -1) {
      setEncodingType(null);
    } else {
      setEncodingType(encodings[index]);
    }
  }, [index, encodings]);

  const handleSelectValue = (value: EncodingMethodsTypes) => {
    setEncodingType({
      method: value,
      metadata: {},
      default: false,
    });
  };

  const { method = EncodingMethodsTypes.LOOKUP } = encodingType || {};
  const encodingMetadataFields = getEncodingMetadataFields(method);

  const handleMetadataChange = (input: Record<string, string | number>) => {
    if (encodingType !== null) {
      setEncodingType({
        ...encodingType,
        metadata: Object.entries(input).reduce((acc, cur) => {
          const [key, value] = cur;
          const { type } = encodingMetadataFields[key];
          let newValue: string | number;
          switch (type) {
            case EncodingMetadataType.NUMBER:
              newValue = typeof value === "number" ? value : parseInt(value, 10);
              break;
            case EncodingMetadataType.STRING:
              newValue = typeof value === "string" ? value : value.toString();
              break;
            default:
              newValue = value;
              break;
          }
          acc[key] = newValue
          return acc;
        }, {} as Record<string, string | number>),
      })
    }
  }

  const handleDialogAction = (sendData: boolean) => {
    if (sendData) {
      handleClose(encodingType);
    } else {
      handleClose(null);
    }
    setEncodingType(null);
  };

  const metadataFields: string[] = Object.keys(encodingMetadataFields);

  const checkFormError = (): string | undefined => {
    if (encodingType !== null && method === EncodingMethodsTypes.SGTIN96) {
      const { metadata } = encodingType;
      return getSGTIN96MetadataError(metadata as Record<string, string | number>);
    }
    if (encodingType !== null && method === EncodingMethodsTypes.LOOKUP) {
      const { metadata } = encodingType;
      return getLookupMetaDataError(metadata as Record<string, string | number>);
    }
    return undefined;
  }

  const formError = checkFormError();

  return (
    <Dialog
      open={index !== undefined}
    >
      <DialogTitle>{index !== -1 ? "Edit" : "Add"} Encoding</DialogTitle>
      <DialogContent>
        <Label
          text="Encoding Method"
        />
        <Select
          fullWidth
          labelId="encoding-label"
          name="encodings"
          id="encoding_dialog"
          value={encodingType !== null ? encodingType.method : "-"}
          inputProps={{ "aria-label": "Without label" }}
        >
          <MenuItem
            key="-"
            value="-"
            disabled
          >
            -
          </MenuItem>
          {selectableEncodings.map(({ key, value, disabled }) => <MenuItem
            key={key}
            value={value}
            disabled={disabled}
            onClick={() => handleSelectValue(value)}
          >
            {value}
          </MenuItem>)}
        </Select>
        {encodingType !== null && <EncodingDialogMetadata
          metadataFields={metadataFields}
          currentData={encodingType.metadata as Record<string, string | number>}
          handleChange={handleMetadataChange}
        />}
        {formError && <HelperText
          text={formError}
          variant={HelperTextVariant.STANDARD}
        />}
      </DialogContent>
      <DialogActions>
        <Button
          disabled={formError !== undefined}
          onClick={() => handleDialogAction(true)}
        >
          {index !== -1 ? "Edit" : "Add"}
        </Button>
        <Button
          onClick={() => handleDialogAction(false)}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}